import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2, QrCode, Smartphone, AlertCircle, Paperclip, FileText, X, Image as ImageIcon, ChevronDown } from "lucide-react";

const QR_STATUS = {
  queued: { label: "In coda", cls: "text-slate-500" },
  processing: { label: "In elaborazione", cls: "text-amber-600" },
  provider_accepted: { label: "Accettato dal provider", cls: "text-sky-600" },
  sent: { label: "Inviato", cls: "text-sky-600" },
  delivered: { label: "Consegnato ✓✓", cls: "text-emerald-600" },
  read: { label: "Letto ✓✓", cls: "text-emerald-600" },
  failed: { label: "Fallito", cls: "text-rose-600" },
};

const QR_ALLOWED = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
const QR_MAX = { image: 5 * 1024 * 1024, document: 16 * 1024 * 1024 };
const qrKind = (mime) => (mime || "").startsWith("image/") ? "image" : "document";
const fmtSize = (b) => b > 1024 * 1024 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;

export default function QuickSend() {
  const navigate = useNavigate();
  const [sendChannel, setSendChannel] = useState("whatsapp");
  const [qrChannelId, setQrChannelId] = useState("qr-primary");
  const [form, setForm] = useState({ channel_id: "", to: "", type: "text", body: "", template_name: "", template_language: "it", variables: {} });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [qrFile, setQrFile] = useState(null);
  const [qrPreview, setQrPreview] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const pollRef = useRef(null);
  const fileRef = useRef(null);
  const submittingRef = useRef(false);

  const { data: chData } = useQuery({ queryKey: ["wa-channels"], queryFn: async () => (await api.get("/whatsapp/channels")).data.data });
  const { data: tplData } = useQuery({ queryKey: ["wa-templates"], queryFn: async () => (await api.get("/whatsapp/templates")).data.data });
  const { data: qrSession } = useQuery({
    queryKey: ["qr-session", qrChannelId],
    queryFn: async () => (await api.get(`/admin/communications/qr-gateway/session?channel_id=${encodeURIComponent(qrChannelId)}`)).data.data,
    enabled: sendChannel === "qr_gateway",
    refetchInterval: sendChannel === "qr_gateway" ? 5000 : false,
  });
  const channels = (chData?.channels || []).filter((c) => c.channel_type === "whatsapp" && !c.deleted_at);
  const templates = (tplData?.templates || []).filter((t) => t.status === "approved");
  const selectedTpl = templates.find((t) => t.name === form.template_name);
  const tplVars = extractVars(selectedTpl);
  const qrConnected = qrSession?.status === "connected";

  useEffect(() => () => { clearInterval(pollRef.current); if (qrPreview) URL.revokeObjectURL(qrPreview); }, [qrPreview]);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!QR_ALLOWED.includes(f.type)) return toast.error("Tipo file non ammesso (JPEG, PNG, WEBP o PDF)");
    const kind = qrKind(f.type);
    if (f.size > QR_MAX[kind]) return toast.error(`File troppo grande (max ${QR_MAX[kind] / 1048576} MB)`);
    if (qrPreview) URL.revokeObjectURL(qrPreview);
    setQrFile(f);
    setQrPreview(f.type.startsWith("image/") ? URL.createObjectURL(f) : null);
  };
  const clearFile = () => { if (qrPreview) URL.revokeObjectURL(qrPreview); setQrFile(null); setQrPreview(null); };

  const startQrPolling = (id) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/communications/messages/${id}`);
        const m = data.data.message;
        const timeline = (data.data.delivery_log || []).map((e) => e.event_type);
        setResult((r) => (r && r.id === id ? { ...r, status: m.status, provider_message_id: m.provider_message_id, error: m.error_message, timeline } : r));
        if (["read", "failed"].includes(m.status)) clearInterval(pollRef.current);
      } catch { /* noop */ }
    }, 2500);
  };

  const submitWhatsApp = async () => {
    if (submittingRef.current) return;
    if (!form.to) return toast.error("Inserisci il destinatario");
    if (form.type === "text" && !form.body) return toast.error("Inserisci il testo");
    if (form.type === "template" && !form.template_name) return toast.error("Seleziona un template");
    if (form.type === "template" && tplVars.some((v) => !form.variables[v])) return toast.error("Compila tutte le variabili del template");
    submittingRef.current = true;
    setLoading(true);
    try {
      const payload = {
        channel_id: form.channel_id || undefined, to: form.to, type: form.type,
        text: form.type === "text" ? { body: form.body } : undefined,
        template: form.type === "template" ? { name: form.template_name, language: form.template_language, variables: form.variables } : undefined,
        idempotency_key: `qs-${Date.now()}`,
      };
      const { data } = await api.post("/whatsapp/messages/send", payload);
      setResult({ mode: "whatsapp", ...data.data });
      toast.success("Messaggio accettato");
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); submittingRef.current = false; }
  };

  const submitQr = async () => {
    if (submittingRef.current) return;
    if (!qrConnected) return toast.error("La sessione QR Gateway non è connessa");
    if (!form.to) return toast.error("Inserisci il destinatario");
    if (!qrFile && !form.body) return toast.error("Inserisci un testo o allega un file");
    submittingRef.current = true;
    setLoading(true);
    try {
      let message_type = "text", media, text = form.body || undefined, caption;
      if (qrFile) {
        const fd = new FormData();
        fd.append("file", qrFile);
        const up = await api.post("/admin/communications/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const m = up.data.data;
        message_type = m.kind;
        media = { media_id: m.media_id, filename: m.filename, mime: m.mime, kind: m.kind };
        caption = form.body || undefined;
        text = undefined;
      }
      const { data } = await api.post("/admin/communications/send", {
        provider: "qr_gateway", channel_id: qrChannelId, to: form.to,
        message_type, text, media, caption, idempotency_key: `qs-qr-${Date.now()}`,
      });
      const msg = data.data.message;
      setResult({ mode: "qr", id: msg.id, status: msg.status, provider_message_id: msg.provider_message_id, timeline: [] });
      toast.success("Messaggio accodato");
      startQrPolling(msg.id);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); submittingRef.current = false; }
  };

  const submit = () => (sendChannel === "qr_gateway" ? submitQr() : submitWhatsApp());

  return (
    <div>
      <PageHeader title="Invio rapido" subtitle="Invia un messaggio WhatsApp al volo" icon={Send} />
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2" data-testid="quicksend-form">
          <CardHeader><CardTitle className="text-base">Nuovo messaggio</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Canale di invio</Label>
              <Select value={sendChannel} onValueChange={(v) => { setSendChannel(v); setResult(null); }}>
                <SelectTrigger data-testid="qs-send-channel"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="whatsapp">WhatsApp Business (Meta / Demo)</SelectItem>
                  <SelectItem value="qr_gateway">QR Gateway (numero collegato)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {sendChannel === "whatsapp" ? (
              <>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Canale WhatsApp</Label>
                    <Select value={form.channel_id} onValueChange={(v) => set("channel_id", v)}>
                      <SelectTrigger data-testid="qs-channel"><SelectValue placeholder="Predefinito" /></SelectTrigger>
                      <SelectContent>{channels.map((c) => <SelectItem key={c.id} value={c.id}>{c.display_name} {c.provider === "demo" ? "(DEMO)" : ""}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Destinatario (E.164)</Label><Input value={form.to} onChange={(e) => set("to", e.target.value)} placeholder="+39333..." data-testid="qs-to" /></div>
                </div>
                <div className="space-y-2">
                  <Label>Tipo messaggio</Label>
                  <Select value={form.type} onValueChange={(v) => set("type", v)}>
                    <SelectTrigger data-testid="qs-type"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="text">Testo</SelectItem><SelectItem value="template">Template</SelectItem></SelectContent>
                  </Select>
                </div>
                {form.type === "text" ? (
                  <div className="space-y-2"><Label>Testo</Label><Textarea rows={4} value={form.body} onChange={(e) => set("body", e.target.value)} data-testid="qs-body" /></div>
                ) : (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Template approvato</Label>
                      <Select value={form.template_name} onValueChange={(v) => { set("template_name", v); const t = templates.find((x) => x.name === v); set("template_language", t?.language || "it"); set("variables", {}); }}>
                        <SelectTrigger data-testid="qs-template"><SelectValue placeholder="Seleziona template" /></SelectTrigger>
                        <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.name}>{t.name} ({t.language})</SelectItem>)}</SelectContent>
                      </Select>
                      {templates.length === 0 && <p className="text-xs text-amber-600">Nessun template approvato. Sincronizza i template dal canale.</p>}
                    </div>
                    {tplVars.map((v) => (
                      <div key={v} className="space-y-2"><Label>Variabile {`{{${v}}}`}</Label><Input value={form.variables[v] || ""} onChange={(e) => set("variables", { ...form.variables, [v]: e.target.value })} data-testid={`qs-var-${v}`} /></div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="rounded-lg border border-border bg-muted/30 p-3" data-testid="qs-qr-session">
                  <div className="flex items-center gap-2">
                    <QrCode className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">Sessione: {qrChannelId}</span>
                    {qrConnected ? (
                      <Badge variant="outline" className="border-emerald-500/40 bg-emerald-500/10 text-emerald-600">Connesso</Badge>
                    ) : (
                      <Badge variant="outline" className="border-amber-500/40 bg-amber-500/10 text-amber-600">{qrSession?.status || "…"}</Badge>
                    )}
                  </div>
                  {qrConnected && qrSession?.phoneNumber && (
                    <p className="mt-1.5 flex items-center gap-1.5 text-xs text-muted-foreground"><Smartphone className="h-3.5 w-3.5" /> Invio dal numero {maskPhone(qrSession.phoneNumber)}</p>
                  )}
                  {!qrConnected && (
                    <p className="mt-1.5 flex items-center gap-1.5 text-xs text-amber-600"><AlertCircle className="h-3.5 w-3.5" /> Collega prima il numero nella pagina QR Gateway.</p>
                  )}
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2"><Label>ID sessione QR</Label><Input value={qrChannelId} onChange={(e) => setQrChannelId(e.target.value)} data-testid="qs-qr-channel" /></div>
                  <div className="space-y-2"><Label>Destinatario (E.164)</Label><Input value={form.to} onChange={(e) => set("to", e.target.value)} placeholder="+39333..." data-testid="qs-to" /></div>
                </div>
                <div className="space-y-2">
                  <Label>{qrFile ? "Didascalia (facoltativa)" : "Testo"}</Label>
                  <Textarea rows={3} value={form.body} onChange={(e) => set("body", e.target.value)} placeholder={qrFile ? "Aggiungi una didascalia…" : "Scrivi il messaggio…"} data-testid="qs-body" />
                </div>
                <div className="space-y-2">
                  <Label>Allegato (immagine o PDF)</Label>
                  <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={onPickFile} className="hidden" data-testid="qs-file-input" />
                  {!qrFile ? (
                    <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="rounded-lg" data-testid="qs-file-pick">
                      <Paperclip className="mr-2 h-4 w-4" /> Allega file
                    </Button>
                  ) : (
                    <div className="flex items-center gap-3 rounded-lg border border-border p-2.5" data-testid="qs-file-preview">
                      {qrPreview ? (
                        <img src={qrPreview} alt="anteprima" className="h-12 w-12 rounded object-cover" />
                      ) : (
                        <div className="flex h-12 w-12 items-center justify-center rounded bg-rose-500/10"><FileText className="h-6 w-6 text-rose-500" /></div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{qrFile.name}</p>
                        <p className="text-xs text-muted-foreground">{qrFile.type} · {fmtSize(qrFile.size)}</p>
                      </div>
                      <Button type="button" variant="ghost" size="icon" onClick={clearFile} data-testid="qs-file-remove"><X className="h-4 w-4" /></Button>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">Ammessi: JPEG, PNG, WEBP (max 5 MB), PDF (max 16 MB).</p>
                </div>
              </>
            )}

            <Button onClick={submit} disabled={loading || (sendChannel === "qr_gateway" && !qrConnected)} className="rounded-lg" data-testid="qs-send">{loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Invia</Button>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card data-testid="quicksend-preview">
            <CardHeader><CardTitle className="text-base">Anteprima</CardTitle></CardHeader>
            <CardContent>
              <div className="rounded-xl bg-[#e5ddd5] p-3 dark:bg-slate-800">
                <div className="ml-auto max-w-[85%] rounded-lg rounded-tr-none bg-[#dcf8c6] p-2.5 text-sm text-slate-900 shadow dark:bg-emerald-900 dark:text-slate-100">
                  {sendChannel === "qr_gateway" && qrFile && (
                    <div className="mb-1.5">
                      {qrPreview ? <img src={qrPreview} alt="allegato" className="max-h-32 rounded" /> : <div className="flex items-center gap-1.5 text-xs"><FileText className="h-4 w-4" /> {qrFile.name}</div>}
                    </div>
                  )}
                  {sendChannel === "qr_gateway" || form.type === "text" ? (form.body || (qrFile ? "" : "Il tuo messaggio...")) : renderTemplate(selectedTpl, form.variables)}
                </div>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">Destinatario: {form.to || "—"}</p>
            </CardContent>
          </Card>

          {result && result.mode === "qr" && (
            <Card className="border-primary/30 bg-primary/5" data-testid="quicksend-result-qr">
              <CardContent className="space-y-2 p-4 text-sm">
                <p className="flex items-center gap-2 font-medium"><CheckCircle2 className="h-4 w-4 text-primary" /> Messaggio inviato (QR Gateway)</p>
                <div className="text-xs">Stato: <span className={`font-medium ${(QR_STATUS[result.status] || {}).cls || ""}`} data-testid="qs-qr-status">{(QR_STATUS[result.status] || {}).label || result.status}</span></div>
                {result.provider_message_id && <p className="break-all text-xs">ID messaggio: {result.provider_message_id}</p>}
                {result.error && <p className="text-xs text-rose-600">Errore: {result.error}</p>}
                {!["read", "failed"].includes(result.status) && <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Aggiornamento stato in tempo reale…</p>}
                {result.timeline?.length > 0 && (
                  <details className="mt-1 text-xs" data-testid="qs-qr-timeline">
                    <summary className="flex cursor-pointer items-center gap-1 text-muted-foreground"><ChevronDown className="h-3 w-3" /> Timeline tecnica</summary>
                    <ol className="mt-1.5 space-y-1 border-l border-border pl-3">
                      {result.timeline.map((ev, i) => <li key={i} className="text-muted-foreground">{ev}</li>)}
                    </ol>
                  </details>
                )}
              </CardContent>
            </Card>
          )}

          {result && result.mode === "whatsapp" && (
            <Card className="border-primary/30 bg-primary/5" data-testid="quicksend-result">
              <CardContent className="space-y-1.5 p-4 text-sm">
                <p className="flex items-center gap-2 font-medium"><CheckCircle2 className="h-4 w-4 text-primary" /> Messaggio accettato</p>
                <div className="text-xs">Stato: <span className="font-medium">{result.status}</span> {result.demo && <Badge variant="outline" className="ml-1 border-violet-500/40 bg-violet-500/10 text-violet-600">DEMO</Badge>}</div>
                <p className="text-xs">Provider message ID: {result.provider_message_id || "(demo)"}</p>
                <Button variant="link" className="h-auto p-0 text-xs" onClick={() => navigate("/conversations")}>Vai alle conversazioni →</Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function maskPhone(p) {
  if (!p) return "—";
  const s = String(p);
  return s.length > 6 ? `${s.slice(0, 4)}••••${s.slice(-2)}` : s;
}

function extractVars(tpl) {
  if (!tpl) return [];
  const body = (tpl.components || []).find((c) => (c.type || "").toUpperCase() === "BODY");
  const text = body?.text || "";
  const matches = [...text.matchAll(/\{\{(\d+)\}\}/g)].map((m) => m[1]);
  return [...new Set(matches)];
}

function renderTemplate(tpl, vars) {
  if (!tpl) return "Seleziona un template...";
  const body = (tpl.components || []).find((c) => (c.type || "").toUpperCase() === "BODY");
  let text = body?.text || tpl.name;
  Object.entries(vars || {}).forEach(([k, v]) => { text = text.replace(`{{${k}}}`, v || `{{${k}}}`); });
  return text;
}
